import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  ModalOverlay, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  Button, 
  Checkbox, 
  Text, 
  VStack, 
  HStack, 
  Box, 
  Divider, 
  Badge, 
  Icon,
  useColorModeValue
} from '@chakra-ui/react';
import { FaBaseballBall, FaCreditCard } from 'react-icons/fa';
import apiClient from '../lib/api/apiClient';
import { useUserStore } from '../store/userStore';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useQueryClient } from '@tanstack/react-query';
import { useModernToast } from './ModernToast';

interface ApiError {
  response?: {
    data?: {
      error?: string;
    };
  };
}

export type ProcessPaymentProps = {
  isOpen: boolean;
  onClose: () => void;
  team_name: string;
  league_name?: string;
  tier?: string;
  price?: number;
  team_id?: number;
  league_id?: number;
  buyer_id?: number;
  onSuccess?: () => void;
};

const ProcessPayment: React.FC<ProcessPaymentProps> = ({
  isOpen,
  onClose,
  team_name,
  league_name,
  tier,
  price,
  team_id,
  league_id,
  buyer_id,
  onSuccess,
}) => {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [invitationCode, setInvitationCode] = useState('');
  const [fatalError, setFatalError] = useState<string | null>(null);
  const toast = useModernToast();
  const { setUser } = useUserStore();
  const { setMyLeague } = useMyLeagueStore();
  const queryClient = useQueryClient();

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const teamCardBg = useColorModeValue('blue.50', 'blue.900');
  const teamCardBorder = useColorModeValue('blue.200', 'blue.700');
  const termsCardBg = useColorModeValue('orange.50', 'orange.900');
  const termsCardBorder = useColorModeValue('orange.200', 'orange.700');
  const checkboxCardBg = useColorModeValue('gray.50', 'gray.700');
  const checkboxTextColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    if (isOpen) {
      setAgreed(false);
      setInvitationCode('');
      setFatalError(null);
    }
  }, [isOpen]);

  const refreshUserData = async () => {
    try {
      // Refresh user data
      const userId = localStorage.getItem('userId');
      const email = localStorage.getItem('email');
      if (userId && email) {
        const userResponse = await apiClient.post('/user/landing', {
          user_id: userId,
          email: email,
        });
        setUser(userResponse.data.user);

        // Fetch and set league data
        if (league_id) {
          const leagueResponse = await apiClient.get(`/leagues/populate/${league_id}`);
          setMyLeague(leagueResponse.data.league);
        }

        // Invalidate relevant queries to force refetch
        queryClient.invalidateQueries({ queryKey: ['userRoles'] });
        queryClient.invalidateQueries({ queryKey: ['roleSettings'] });
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const handlePay = async () => {
    if (!team_id || !league_id) {
      toast.error('Error', 'Missing team or league information');
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiClient.post('/team/acquire-team', {
        team_id,
        league_id,
        buyer_id,
        code: invitationCode,
      });

      if (response.data.success) {
        // Refresh user data and context
        await refreshUserData();

        toast.success('Success', `Successfully acquired ${team_name}!`);
        if (onSuccess) onSuccess();
        onClose();
      } else {
        // If error, show fatal error dialog
        setFatalError(response.data.error || 'Failed to acquire team');
      }
    } catch (error: unknown) {
      console.error('Error acquiring team:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to acquire team';
      const apiError = (error as ApiError)?.response?.data?.error;
      setFatalError(apiError || errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const isFree = price === 0;
  const dialogTitle = isFree
    ? `Assume ownership of ${team_name}`
    : `Entry Fee Payment for ${team_name}`;

  // Validation logic
  const isCodeValid = invitationCode.length >= 5;
  const isFormValid = agreed && isCodeValid;

  if (fatalError) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent maxW="400px" borderRadius="xl" boxShadow="2xl">
          <ModalHeader>{dialogTitle}</ModalHeader>
          <ModalBody>
            <Box 
              p={4} 
              bg={termsCardBg} 
              borderRadius="lg"
              border="1px solid"
              borderColor={termsCardBorder}
              mb={2}
            >
              <VStack spacing={3} align="stretch">
                <Text fontWeight="semibold" color={textColor} fontSize="sm">
                  <span role="img" aria-label="warning">⚠️</span> Important Message
                </Text>
                <Text color={textColor} fontWeight="medium">We're sorry, but we were unable to process your ownership request.</Text>
                <Text color={textColor} fontWeight="medium">{fatalError}</Text>
              </VStack>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false} closeOnEsc={false} size="md">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent 
        bg={bgColor} 
        border="1px solid" 
        borderColor={borderColor}
        borderRadius="xl"
        boxShadow="2xl"
        maxW="500px"
      >
        <ModalHeader 
          borderBottom="1px solid" 
          borderColor={borderColor}
          pb={4}
          display="flex"
          alignItems="center"
          gap={3}
        >
          <Icon as={isFree ? FaBaseballBall : FaCreditCard} color={accentColor} boxSize={5} />
          <Text fontSize="lg" fontWeight="bold" color={textColor}>
            {dialogTitle}
          </Text>
        </ModalHeader>
        
        <ModalBody py={6}>
          <VStack spacing={6} align="stretch">
            {/* Team Information Card */}
            <Box 
              p={4} 
              bg={teamCardBg} 
              borderRadius="lg"
              border="1px solid"
              borderColor={teamCardBorder}
            >
              <VStack spacing={3} align="stretch">
                <HStack justify="space-between" align="center">
                  <Text fontWeight="semibold" fontSize="md" color={textColor}>
                    Team Details
                  </Text>
                  <Badge 
                    colorScheme={isFree ? 'green' : 'blue'} 
                    variant="subtle"
                    px={3}
                    py={1}
                    borderRadius="full"
                  >
                    {tier?.toUpperCase() || 'STANDARD'}
                  </Badge>
                </HStack>
                
                <Divider />
                
                <VStack spacing={2} align="stretch">
                  <HStack justify="space-between">
                    <Text color={textColor} fontSize="sm">Team Name:</Text>
                    <Text fontWeight="semibold" color={textColor}>{team_name}</Text>
                  </HStack>
                  
                  {league_name && (
                    <HStack justify="space-between">
                      <Text color={textColor} fontSize="sm">League:</Text>
                      <Text fontWeight="semibold" color={textColor}>{league_name}</Text>
                    </HStack>
                  )}
                  
                  {price !== undefined && (
                    <HStack justify="space-between">
                      <Text color={textColor} fontSize="sm">Entry Fee:</Text>
                      <Text 
                        fontWeight="bold" 
                        fontSize="lg" 
                        color={isFree ? 'green.500' : accentColor}
                      >
                        {isFree ? 'FREE' : `$${price}`}
                      </Text>
                    </HStack>
                  )}
                </VStack>
              </VStack>
            </Box>

            {/* Invitation Code Section */}
            <Box 
              p={4} 
              bg={teamCardBg} 
              borderRadius="lg"
              border="1px solid"
              borderColor={teamCardBorder}
            >
              <VStack spacing={3} align="stretch">
                <Text fontWeight="semibold" color={textColor} fontSize="sm">
                  <span role="img" aria-label="key">🔑</span> Beta Testing Access Code Required
                </Text>
                <HStack spacing={3} align="center">
                  <Text fontSize="sm" fontWeight="medium" color={textColor} minW="48px">
                    CODE:
                  </Text>
                  <input
                    type="text"
                    value={invitationCode}
                    onChange={(e) => setInvitationCode(e.target.value)}
                    placeholder="Enter your invitation code"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: `1px solid ${isCodeValid ? '#48BB78' : '#E53E3E'}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: 'transparent',
                      color: textColor,
                      outline: 'none',
                      transition: 'border-color 0.2s ease-in-out'
                    }}
                  />
                </HStack>
                {invitationCode.length > 0 && (
                  <Text 
                    fontSize="xs" 
                    color={isCodeValid ? 'green.500' : 'red.500'}
                    fontWeight="medium"
                  >
                    {isCodeValid ? '✓ Valid code format' : '✗ Code must be at least 5 characters'}
                  </Text>
                )}
              </VStack>
            </Box>

            {/* Terms and Conditions */}
            {!isFree && (
              <Box 
                p={4} 
                bg={termsCardBg} 
                borderRadius="lg"
                border="1px solid"
                borderColor={termsCardBorder}
              >
                <VStack spacing={3} align="stretch">
                  <Text fontWeight="semibold" color={textColor} fontSize="sm">
                    ⚠️ Important Terms
                  </Text>
                  <Text fontSize="sm" color={textColor} lineHeight="tall">
                    To own this team, you must pay the entry fee within <strong>five days</strong>. 
                    If payment is not completed within this timeframe, the team will be made available to other users.
                  </Text>
                </VStack>
              </Box>
            )}

            {/* Agreement Checkbox */}
            <Box 
              p={4} 
              bg={checkboxCardBg} 
              borderRadius="lg"
              border="1px solid"
              borderColor={borderColor}
            >
              <Checkbox
                isChecked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                colorScheme="blue"
                size="lg"
                spacing={3}
              >
                <VStack align="start" spacing={1}>
                  <Text fontWeight="medium" color={textColor}>
                    {tier && tier.toLowerCase() === 'free'
                      ? 'I agree to take ownership of this free team'
                      : price != null
                        ? `I agree to pay the $${price} entry fee`
                        : 'I agree to pay the entry fee for this team'}
                  </Text>
                  <Text fontSize="xs" color={checkboxTextColor}>
                    By checking this box, you confirm your agreement to the terms and conditions.
                  </Text>
                </VStack>
              </Checkbox>
            </Box>
          </VStack>
        </ModalBody>
        
        <ModalFooter 
          borderTop="1px solid" 
          borderColor={borderColor}
          pt={4}
          gap={3}
        >
          <Button 
            variant="outline" 
            onClick={onClose}
            size="md"
            flex={1}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handlePay}
            isDisabled={!isFormValid || submitting}
            isLoading={submitting}
            loadingText={isFree ? "Processing..." : "Processing Payment..."}
            leftIcon={isFree ? <FaBaseballBall /> : <FaCreditCard />}
            size="md"
            flex={1}
          >
            {tier && tier.toLowerCase() === 'free' ? 'Take Ownership' : 'Pay Now'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ProcessPayment; 